import React from "react";

const VELVET_BG =
  "https://static.prod-images.emergentagent.com/jobs/12ae9d75-2045-46a7-a3b7-9b9136f324b8/images/c0b4f37abec352d74430959ca0360ac83cb4258abe85540adfaae658a978a4cb.png";

const SENDER_EMAIL = "andrea@codex.soulfemmecodex.com";

const firstNameOf = (full) => {
  if (!full) return "";
  return String(full).trim().split(/\s+/)[0];
};

/**
 * Post-payment confirmation panel.
 * The Codex is delivered exclusively via email. This page never offers a
 * download — its only job is to (a) reassure the buyer that the report is
 * being prepared, (b) tell her exactly when and from whom to expect it,
 * and (c) coach her to whitelist + check spam.
 */
export const PaidEmailPanel = ({
  reading,
  expansionStatus,
  emailStatus,
  onResendEmail,
  onRetryExpansion,
}) => {
  const name = reading?.name;
  const email = reading?.email;
  const firstName = firstNameOf(name);

  const subjectLine = firstName
    ? `${firstName}, your Codex is ready`
    : "Your Codex is ready";

  // Granular states the user can land in:
  //  - processing : weaving in flight (still under the 12-min watchdog)
  //  - failed     : weaving timed out / errored — show retry CTA
  //  - sent       : email confirmed delivered
  //  - other      : weaving done but email not yet sent (rare; resend CTA)
  const isProcessing = expansionStatus === "processing";
  const isFailed = expansionStatus === "failed";
  const isSent = emailStatus === "sent";
  const isEmailFailed = emailStatus === "failed" || emailStatus === "no_recipient";

  let badge = "Codex · Preparing";
  let headline = (
    <>
      Your Codex is being{" "}
      <span className="italic" style={{ color: "var(--sf-leopard-gold)" }}>
        prepared
      </span>
    </>
  );
  let primaryCopy = (
    <>
      Your Codex is extensive. Andrea is weaving it now — give it about{" "}
      <strong>15 minutes</strong>. It will arrive in your inbox the moment
      it's ready.
    </>
  );
  if (isFailed) {
    badge = "Codex · Paused";
    headline = (
      <>
        The weaving{" "}
        <span className="italic" style={{ color: "var(--sf-leopard-gold)" }}>
          paused
        </span>
      </>
    );
    primaryCopy = (
      <>
        The AI that translates your design was running slow. Your payment is
        safe — tap the button below to weave again. It usually takes 5–15
        minutes.
      </>
    );
  } else if (isSent) {
    badge = "Codex · Delivered";
    headline = (
      <>
        Your Codex has been{" "}
        <span className="italic" style={{ color: "var(--sf-leopard-gold)" }}>
          sent
        </span>
      </>
    );
    primaryCopy = (
      <>
        It just landed in your inbox. If you don't see it within a few minutes,
        check your spam or junk folder.
      </>
    );
  }

  return (
    <section
      className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28"
      data-testid="paid-email-panel"
    >
      <div className="text-center mb-10">
        <div
          className="inline-block px-5 py-1.5 mb-6 rounded-full border border-[var(--sf-leopard-gold)] text-xs tracking-[0.3em] uppercase text-[var(--sf-leopard-gold)]"
          data-testid="email-panel-badge"
        >
          {badge}
        </div>
        <div className="overline mb-5">Your Soul Femme Codex</div>
        <h1 className="font-display text-5xl md:text-6xl mb-6 leading-tight text-[var(--sf-ivory)]">
          {headline}
        </h1>
      </div>

      <div className="relative rounded-2xl overflow-hidden p-10 md:p-16 border border-[var(--sf-border-strong)]">
        <img
          src={VELVET_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0C10]/55 via-[#4A0E4E]/45 to-[#0B0C10]/80" />
        <div className="relative space-y-7 text-center">
          <p className="codex-text max-w-xl mx-auto" data-testid="primary-copy">
            {primaryCopy}
          </p>

          {!isFailed && (
            <div
              className="text-left max-w-md mx-auto bg-[rgba(11,12,16,0.55)] rounded-xl border border-[var(--sf-border)] p-6 space-y-4"
              data-testid="email-meta-card"
            >
              {email && (
                <div>
                  <div className="overline mb-1">Delivering to</div>
                  <div
                    className="codex-text break-all"
                    data-testid="email-meta-recipient"
                  >
                    {email}
                  </div>
                </div>
              )}
              <div>
                <div className="overline mb-1">From</div>
                <div className="codex-text" data-testid="email-meta-from">
                  {SENDER_EMAIL}
                </div>
              </div>
              <div>
                <div className="overline mb-1">Subject</div>
                <div
                  className="codex-text italic"
                  data-testid="email-meta-subject"
                >
                  {subjectLine}
                </div>
              </div>
            </div>
          )}

          {!isFailed && (
            <p
              className="text-sm leading-relaxed text-[var(--sf-text-secondary)] max-w-xl mx-auto"
              data-testid="whitelist-note"
            >
              Please whitelist{" "}
              <span className="text-[var(--sf-leopard-gold)]">
                {SENDER_EMAIL}
              </span>{" "}
              and check your <strong>spam</strong> or <strong>junk</strong>{" "}
              folder if it doesn't appear in your inbox.
            </p>
          )}

          {isProcessing && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <span className="sf-pulse-dot" />
              <span className="overline">Weaving in progress</span>
            </div>
          )}

          {isFailed && (
            <div className="pt-2">
              <button
                type="button"
                className="sf-btn-primary"
                onClick={onRetryExpansion}
                data-testid="paid-failed-retry-button"
              >
                Re-weave My Codex
              </button>
            </div>
          )}

          {!isProcessing && !isFailed && (
            <div className="pt-2 print:hidden">
              <button
                type="button"
                className="sf-btn-ghost"
                onClick={onResendEmail}
                data-testid="resend-email-button"
              >
                {isSent ? "Resend the email" : "Send my Codex now"}
              </button>
            </div>
          )}

          {isEmailFailed && (
            <p
              className="text-xs uppercase tracking-widest text-[var(--sf-leopard-gold)]"
              data-testid="email-failed-notice"
            >
              The email didn't go through on the first try — tap above to send
              it again.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default PaidEmailPanel;
